const env = import.meta.env as Record<string, string | undefined>;
export const getEnv = (viteKey: string) => env[viteKey];

export const getAbbreviation = (word: string) => {
  let result = '';
  const words = word ? word.split(' ') : '';
  if (words.length === 1) {
    result = word;
  } else if (words.length === 2) {
    const firstWord = words[0];
    if (firstWord[0] === firstWord[0]?.toUpperCase() && firstWord[1] === firstWord[1]?.toUpperCase()) {
      result = firstWord;
    } else {
      for (const w of words) {
        result += w[0]?.toUpperCase();
      }
    }
  } else {
    for (const w of words) {
      result += w[0]?.toUpperCase();
    }
  }

  return result;
};

export const scrollToView = (id: string) => {
  document.getElementById(id)?.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
  });
};

export const removeDuplicateSpacesFromText = (sentence: string) => {
  // Trim spaces from the start and end of the string
  // Replace multiple spaces between words with a single space
  return sentence.trim().replace(/\s+/g, ' ');
};

export const convertCamelCaseToNormalText = (input: string) => {
  if (input) {
    let text = input.replace(/([a-z])([A-Z])/g, '$1 $2');

    text = text.charAt(0).toUpperCase() + text.slice(1);

    return text;
  }
};

export const sanitizeHTML = (html: string) => {
  const div = document.createElement('div');
  div.innerHTML = html;

  div.querySelectorAll('script, iframe, object, embed').forEach(el => el.remove());

  div.querySelectorAll('*').forEach(el => {
    Array.from(el.attributes)
      .filter(attr => attr.name.startsWith('on'))
      .forEach(attr => el.removeAttribute(attr.name));
  });

  div.querySelectorAll('p').forEach(p => {
    if (!p.textContent?.trim()) p.innerHTML = '&nbsp;';
  });

  return div.innerHTML;
};

export function formatMeetLink(link: string) {
  if (!link.startsWith('http://') && !link.startsWith('https://')) {
    link = 'https://' + link;
  }
  return link;
}

export const getShortName = (value: string | undefined | null) => {
  return value?.split(/\s/).reduce((res, word) => (res += word.slice(0, 1)), '');
};

export const getInterviewStatusBadgeClass = (status: string | undefined): string => {
  if (!status) return 'badges-gray';
  const map: Record<string, string> = {
    COMPLETED: 'badges-green',
    SCHEDULED: 'badges-blue',
    IN_PROGRESS: 'badges-yellow',
  };
  return map[status] ?? 'badges-gray';
};

import { BOT_STATUS } from '@/constants/botStatus';

export const getBotStatusBgClass = (status: string | undefined): string => {
  if (!status) return 'bg-gray-700';
  const map: Record<string, string> = {
    [BOT_STATUS.JOINING_CALL]: 'badges-yellow',
    [BOT_STATUS.IN_WAITING_ROOM]: 'bg-blue',
    [BOT_STATUS.IN_CALL_NOT_RECORDING]: 'bg-gray-700',
    [BOT_STATUS.IN_CALL_RECORDING]: 'bg-success',
    [BOT_STATUS.CALL_ENDED]: 'bg-danger',
  };
  return map[status] ?? 'bg-gray-700';
};

export const getBotStatusBadgeClass = (status: string | undefined): string => {
  if (!status) return 'bg-gray-700';
  const map: Record<string, string> = {
    [BOT_STATUS.JOINING_CALL]: 'badges-yellow--light',
    [BOT_STATUS.IN_WAITING_ROOM]: 'badges-blue--light',
    [BOT_STATUS.IN_CALL_NOT_RECORDING]: 'badges-gray--light',
    [BOT_STATUS.IN_CALL_RECORDING]: 'badges-green--light',
    // `pe-0` so the Reconnect button inside the badge sits flush against its edge.
    [BOT_STATUS.CALL_ENDED]: 'badges-red--light pe-0',
  };
  return map[status] ?? 'badges-gray--light';
};
export const getOverAllScoreBandBadgeClass = (band: string | undefined): string => {
  if (!band) return 'badges-gray';
  const map: Record<string, string> = {
    'Ready for next round': 'badges-green--light',
    'Needs further review': 'badges-blue--light',
    'Not Recommended': 'badges-red--light',
  };
  return map[band] ?? 'badges-gray';
};

export const getOverAllScoreBandForReportBadgeClass = (band: string | undefined): string => {
  if (!band) return '#dbdbdf';
  const map: Record<string, string> = {
    'Ready for next round': '#6EC57B',
    'Needs further review': '#5B90EB',
    'Not Recommended': '#FF4C4C',
  };
  return map[band] ?? '#dbdbdf';
};

export const getScoreBandFromValue = (score: number): string => {
  if (score >= 7) return 'Ready for next round';
  if (score >= 4) return 'Needs further review';
  return 'Not Recommended';
};

export const calendar = [
  {
    icon: '/images/google-calendar.svg',
    title: 'Google Calendar',
    description: 'Create, update, and delete events across your calendars',
    provider: 'google',
    hidden: false,
  },
  {
    icon: '/images/zoom.svg',
    title: 'Zoom',
    description: 'Add, update events to your zoom meetings.',
    provider: 'zoom',
    hidden: true,
  },
  {
    icon: '/images/outlook.svg',
    title: 'Microsoft Outlook / Teams',
    description: 'Schedule events, sync meeting details, and manage calendars.',
    provider: 'microsoft',
    hidden: false,
  },
];

export const visibleCalendar = calendar.filter(calendarItem => !calendarItem.hidden);
